import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { logger } from "@/utils/logger";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const EXPECTED_ADMIN_EMAIL = "3d-druck-bolz@gmx.de";

type CheckStatus = "pending" | "ok" | "fail" | "warn";

interface DiagnosticCheck {
  id: string;
  label: string;
  status: CheckStatus;
  detail?: string;
  hint?: string;
  raw?: unknown;
}

/** Klartext-Übersetzung typischer Postgres/PostgREST-Fehler. */
function explainError(err: { code?: string; message?: string } | null | undefined): {
  detail: string;
  hint?: string;
} {
  if (!err) return { detail: "Unbekannter Fehler" };
  const msg = err.message ?? "";
  const code = err.code ?? "";

  // permission denied for function has_role  → EXECUTE-Grant fehlt
  if (/permission denied for function has_role/i.test(msg) || code === "42501") {
    return {
      detail: `${msg}${code ? ` (SQLSTATE ${code})` : ""}`,
      hint:
        'Der Datenbankrolle "authenticated" fehlt das EXECUTE-Recht auf die Funktion ' +
        "public.has_role(uuid, app_role). Lösung (als Migration): " +
        'GRANT EXECUTE ON FUNCTION public.has_role(uuid, public.app_role) TO authenticated, anon;',
    };
  }

  // RLS / Policy
  if (/row-level security|new row violates row-level security/i.test(msg) || code === "42501") {
    return {
      detail: `${msg}${code ? ` (SQLSTATE ${code})` : ""}`,
      hint:
        "Eine Row-Level-Security-Policy verweigert den Zugriff. " +
        "Prüfe die Policies auf user_roles bzw. die has_role-Funktion.",
    };
  }

  // JWT abgelaufen
  if (/jwt expired|invalid jwt|jwt is expired/i.test(msg)) {
    return {
      detail: msg,
      hint: "Die Session ist abgelaufen. Bitte abmelden und neu anmelden.",
    };
  }

  return { detail: `${msg}${code ? ` (Code ${code})` : ""}` };
}

export const ProtectedRoute = ({ children, requireAdmin = false }: ProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [roleError, setRoleError] = useState<{ message: string; code?: string } | null>(null);
  const [roleData, setRoleData] = useState<unknown>(null);
  const [checks, setChecks] = useState<DiagnosticCheck[]>([]);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const navigate = useNavigate();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkAdminRole = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        logger.error("Error checking admin role:", error);
        setRoleError({ message: error.message, code: (error as { code?: string }).code });
        setIsAdmin(false);
        return false;
      }
      setRoleData(data);
      const ok = !!data;
      setIsAdmin(ok);
      return ok;
    } catch (error) {
      logger.error("Error checking admin role:", error);
      setRoleError({
        message: error instanceof Error ? error.message : String(error),
      });
      setIsAdmin(false);
      return false;
    }
  }, []);

  /** Volle Selbstdiagnose – läuft automatisch sobald Access-Denied angezeigt wird. */
  const runDiagnostics = useCallback(async () => {
    setIsDiagnosing(true);
    const results: DiagnosticCheck[] = [];

    // 1. Session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const sess = sessionData?.session;
    if (sessionError || !sess) {
      results.push({
        id: "session",
        label: "Aktive Session",
        status: "fail",
        detail: sessionError?.message ?? "Keine Session vorhanden",
        hint: "Bitte erneut anmelden.",
      });
    } else {
      const expiresIn = sess.expires_at ? sess.expires_at * 1000 - Date.now() : null;
      results.push({
        id: "session",
        label: "Aktive Session",
        status: "ok",
        detail: `${sess.user.email}${
          expiresIn !== null
            ? ` · läuft ab in ${Math.max(0, Math.round(expiresIn / 1000 / 60))} min`
            : ""
        }`,
      });
    }

    // 2. Email-Match (Hinweis, kein Hardfail)
    if (sess?.user?.email) {
      const matches = sess.user.email.toLowerCase() === EXPECTED_ADMIN_EMAIL.toLowerCase();
      results.push({
        id: "email-match",
        label: "Erwartete Admin-Adresse",
        status: matches ? "ok" : "warn",
        detail: matches
          ? `Eingeloggt als ${sess.user.email}`
          : `Eingeloggt als ${sess.user.email}, erwartet ${EXPECTED_ADMIN_EMAIL}`,
        hint: matches
          ? undefined
          : "Falls dieser Account keine Admin-Rolle hat, mit der erwarteten Adresse neu anmelden.",
      });
    }

    // 3. Direktes SELECT auf user_roles (zeigt RLS-Verhalten)
    if (sess?.user?.id) {
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", sess.user.id);

      if (rolesError) {
        const ex = explainError(rolesError);
        results.push({
          id: "user-roles-select",
          label: "Lesezugriff auf user_roles",
          status: "fail",
          detail: ex.detail,
          hint: ex.hint,
          raw: rolesError,
        });
      } else {
        const roles = (rolesData ?? []).map((r: { role: string }) => r.role);
        const hasAdmin = roles.includes("admin");
        results.push({
          id: "user-roles-select",
          label: "Lesezugriff auf user_roles",
          status: hasAdmin ? "ok" : "warn",
          detail:
            roles.length === 0
              ? "Keine Rollen in user_roles gefunden"
              : `Gefundene Rollen: ${roles.join(", ")}`,
          hint: hasAdmin
            ? undefined
            : "Dieser Account hat keine admin-Rolle. Ein bestehender Admin muss sie über die Datenbank vergeben.",
        });
      }
    }

    // 4. RPC has_role(...) – deckt explizit den EXECUTE-Grant-Fehler auf
    if (sess?.user?.id) {
      const { data: rpcData, error: rpcError } = await supabase.rpc("has_role", {
        _user_id: sess.user.id,
        _role: "admin",
      });

      if (rpcError) {
        const ex = explainError(rpcError);
        results.push({
          id: "has-role-rpc",
          label: "Funktion has_role() ausführbar",
          status: "fail",
          detail: ex.detail,
          hint: ex.hint,
          raw: rpcError,
        });
      } else {
        results.push({
          id: "has-role-rpc",
          label: "Funktion has_role() ausführbar",
          status: rpcData ? "ok" : "warn",
          detail: rpcData
            ? "Funktion liefert true – Admin-Rolle bestätigt."
            : "Funktion liefert false – kein Admin.",
        });
      }
    }

    setChecks(results);
    setIsDiagnosing(false);
  }, []);

  const enforceAal2 = useCallback(async () => {
    if (!requireAdmin) return true;
    // Loop-Schutz: auf /admin-mfa selbst niemals weiter zu /admin-mfa redirecten.
    if (window.location.pathname.startsWith("/admin-mfa")) return true;
    try {
      const [{ data: aal }, { data: factors }] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);
      const currentLevel = aal?.currentLevel;
      const hasVerifiedTotp = !!factors?.totp?.some((f) => f.status === "verified");

      // Kein verifizierter TOTP-Faktor → Enrollment erzwingen.
      // Verifizierter TOTP vorhanden, aber Session nur AAL1 → Challenge erzwingen.
      if (!hasVerifiedTotp || currentLevel !== "aal2") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/admin-mfa?next=${next}`, { replace: true });
        return false;
      }
      return true;
    } catch (e) {
      logger.error("enforceAal2 error:", e);
      // Fail-closed für Admin: MFA-Enrollment/Challenge erzwingen.
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/admin-mfa?next=${next}`, { replace: true });
      return false;
    }
  }, [navigate, requireAdmin]);

  useEffect(() => {
    const handleSession = async (session: { user: { id: string } } | null) => {
      setUser((session?.user as User | undefined) ?? null);

      if (!session?.user) {
        navigate("/auth");
        return;
      }

      if (!requireAdmin) {
        setIsLoading(false);
        return;
      }

      // 1) Erst Admin-Rolle prüfen (Nicht-Admins dürfen keinen Admin-MFA-Faktor enrollen).
      const ok = await checkAdminRole(session.user.id);
      if (!ok) {
        setIsLoading(false);
        return;
      }
      // 2) Nur für Admins AAL2 erzwingen (Enrollment/Challenge).
      const aal2Ok = await enforceAal2();
      if (!aal2Ok) return; // Redirect läuft.
      setIsLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        handleSession(session);
      }, 0);
    });

    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [navigate, requireAdmin, checkAdminRole, enforceAal2]);


  // Diagnose automatisch starten, sobald Access-Denied feststeht
  useEffect(() => {
    if (!isLoading && requireAdmin && user && !isAdmin && checks.length === 0 && !isDiagnosing) {
      runDiagnostics();
    }
  }, [isLoading, requireAdmin, user, isAdmin, checks.length, isDiagnosing, runDiagnostics]);

  const handleHardSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      logger.error("signOut error:", e);
    }
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sb-") || k.includes("supabase"))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (e) {
      logger.error("storage clear error:", e);
    }
    navigate("/auth");
    window.location.reload();
  };

  const handleRetryAdminCheck = () => {
    if (!user) return;
    setRoleError(null);
    setRoleData(null);
    setIsLoading(true);
    setChecks([]);
    checkAdminRole(user.id);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  if (requireAdmin && !isAdmin) {
    const showDiagnostics = import.meta.env.DEV;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Zugriff verweigert</h1>
          <p className="text-muted-foreground mb-6">
            Du hast keine Berechtigung für diesen Bereich.
          </p>

          {showDiagnostics && (
            <div className="text-left bg-muted/40 border border-border rounded-md p-4 text-xs space-y-2 mb-6">
              <div className="font-semibold text-sm">Diagnose (nur Development)</div>
              <div>
                <span className="font-semibold">User:</span> <span className="font-mono break-all">{user.email ?? "—"}</span>
              </div>
              {roleError && (
                <div className="text-destructive">
                  {explainError(roleError).detail}
                </div>
              )}
              <div className="pt-2">
                <button
                  onClick={runDiagnostics}
                  disabled={isDiagnosing}
                  className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${isDiagnosing ? "animate-spin" : ""}`} />
                  Selbstdiagnose ausführen
                </button>
              </div>
              {checks.map((c) => (
                <div key={c.id} className="pt-1">
                  <div className="font-medium">{c.label}: {c.status}</div>
                  {c.detail && <div className="text-muted-foreground">{c.detail}</div>}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={handleHardSignOut}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-sm"
            >
              Abmelden
            </button>
            <button
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded-md border border-border text-foreground hover:bg-muted text-sm"
            >
              Zurück zur Startseite
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
